import { useState, useEffect } from "react";
import {
  DeviceContext,
  getDeviceSnapshot,
  getNavigatorConnection,
  addMediaQueryChangeListener,
  removeMediaQueryChangeListener,
  type DeviceInfo,
} from "./useDevice";

interface DeviceProviderProps {
  children: React.ReactNode;
}

/**
 * Mount once near the root of the app. All useDevice() calls inside will
 * read from this context instead of each registering their own event listeners.
 */
export const DeviceProvider = ({ children }: DeviceProviderProps) => {
  const [info, setInfo] = useState<DeviceInfo>(getDeviceSnapshot);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => setInfo(getDeviceSnapshot());

    // Resize / orientation
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    if (screen.orientation) {
      screen.orientation.addEventListener("change", update);
    }

    // Online / offline
    window.addEventListener("online", update);
    window.addEventListener("offline", update);

    // Network info
    const conn = getNavigatorConnection();
    if (conn?.addEventListener) {
      conn.addEventListener("change", update);
    }

    // Media query listeners
    const queries = [
      "(pointer: fine)",
      "(pointer: coarse)",
      "(hover: hover)",
      "(prefers-reduced-motion: reduce)",
      "(prefers-color-scheme: dark)",
      "(prefers-contrast: more)",
      "(prefers-reduced-transparency: reduce)",
      "(display-mode: standalone)",
    ].map((q) => {
      const mql = window.matchMedia(q);
      addMediaQueryChangeListener(mql, update);
      return mql;
    });

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      screen.orientation?.removeEventListener("change", update);
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      if (conn?.removeEventListener) {
        conn.removeEventListener("change", update);
      }
      queries.forEach((mql) => removeMediaQueryChangeListener(mql, update));
    };
  }, []);

  return <DeviceContext.Provider value={info}>{children}</DeviceContext.Provider>;
};
